import { apiClient } from '@/lib/api/client';
import type {
  Promotion,
  PromotionCode,
  PromotionFilters,
  PromotionStats,
  CreatePromotionData,
  UpdatePromotionData,
  PromotionStatus,
} from '../types/promotions.types';

interface PromotionListResponse {
  data: Promotion[];
  total: number;
  page: number;
  lastPage: number;
}

export const promotionsService = {
  async getPromotions(filters?: PromotionFilters): Promise<{ promotions: Promotion[]; total: number }> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    params.set('page', String(filters?.page || 1));
    params.set('limit', String(filters?.limit || 20));
    if (filters?.status) params.set('status', filters.status);

    const response = await apiClient.get<PromotionListResponse>(`/promotions?${params.toString()}`);
    return {
      promotions: response.data.data,
      total: response.data.total,
    };
  },

  async getPromotion(refId: string): Promise<Promotion> {
    const response = await apiClient.get<Promotion>(`/promotions/${refId}`);
    return response.data;
  },

  async createPromotion(data: CreatePromotionData): Promise<Promotion> {
    const response = await apiClient.post<Promotion>('/promotions', data);
    return response.data;
  },

  async updatePromotion(refId: string, data: UpdatePromotionData): Promise<Promotion> {
    const response = await apiClient.put<Promotion>(`/promotions/${refId}`, data);
    return response.data;
  },

  async updatePromotionStatus(refId: string, status: PromotionStatus): Promise<Promotion> {
    const response = await apiClient.patch<Promotion>(`/promotions/${refId}/status`, { status });
    return response.data;
  },

  async deletePromotion(refId: string): Promise<void> {
    await apiClient.delete(`/promotions/${refId}`);
  },

  async getPromotionStats(refId: string): Promise<PromotionStats> {
    const response = await apiClient.get<PromotionStats>(`/promotions/${refId}/stats`);
    return response.data;
  },

  async getPromotionCodes(promotionRefId: string): Promise<PromotionCode[]> {
    const response = await apiClient.get<PromotionCode[]>(`/promotions/${promotionRefId}/codes`);
    return response.data;
  },

  async createPromotionCode(
    promotionRefId: string,
    data: { code?: string; maxUses?: number }
  ): Promise<PromotionCode> {
    const response = await apiClient.post<PromotionCode>(`/promotions/${promotionRefId}/codes`, data);
    return response.data;
  },

  async deletePromotionCode(codeRefId: string): Promise<void> {
    await apiClient.delete(`/promotions/codes/${codeRefId}`);
  },
};
